import { NzTableFilterFn } from 'ng-zorro-antd/table';
import {
    BITableOptions,
    ITableDashboardItemComponent,
    TableColumnItem,
    TableDataItem,
} from 'src/app/components/table-dashboard-item/table-dashboard-item.component';
import {
    IQueryContext,
    IQueryContextDatasourceColumn,
} from 'src/app/services/query-context/models/IQueryContext';
import { getDatasourceColumnName } from 'src/app/utils/datasource-sql-map/datasource';
import { generateGradientColors } from 'src/app/utils/ng-zorro';

/**
 * Helper class to create TableDashboardItem
 */
export class TableDashboardItemHelper {
    static getTableDashboardItem(options: {
        dashboardName: string;
        /** Id of created instance */
        instanceId: string;
        // FACTORY_CLASS: string;
        dashboardItem: {
            title: string | ((context: IQueryContext) => string);
            subtitle?: string;
            /** HTML string for chart documentation */
            chartDocumentation?: string;
            /** A property that allows you to choose between a table divided into pages and a table that dynamically adjusts to the size of the browser window with scrolling.
             *
             * @default 'paged'
             */
            viewMode?: 'paged' | 'scroll';
        };
        /** Whether to add columns for splits based on QueryContext */
        splitTableByQueryContext?: boolean;
        /** Function to get metrics for table */
        columnsGetter: (context: IQueryContext) => TableColumnItem[];
        splitColumnsSortFn?: (a: TableColumnItem, b: TableColumnItem) => number;
        /** Index into columnsGetter's columns after which the split columns are
         * inserted. Default 0 = split columns come first (before every predefined
         * column) — the existing behavior. */
        splitColumnsPosition?: number;
        /** ng-zorro specific. Table page size */
        nzPageSize?: number;
    }) {
        const composeData =
            TableDashboardItemHelper.composeDataFnGetter(options);
        const tableDashboardItem: ITableDashboardItemComponent = {
            dashboardName: options.dashboardName,
            instanceId: options.instanceId,
            dashboardItem: options.dashboardItem,
            title: options.dashboardItem.title,
            subtitle: options.dashboardItem.subtitle,
            viewMode: options.dashboardItem.viewMode ?? 'paged',
            component: 'TableDashboardItemComponent',
            composeData: composeData,
            nzPageSize: options.nzPageSize,
            chartDocumentation: options.dashboardItem.chartDocumentation,
        };
        return tableDashboardItem;
    }

    static composeDataFnGetter(options: {
        /** Whether to add columns for splits based on QueryContext */
        splitTableByQueryContext?: boolean;
        /** Function to get columns for table */
        columnsGetter: (context: IQueryContext) => TableColumnItem[];
        splitColumnsSortFn?: (a: TableColumnItem, b: TableColumnItem) => number;
        /** Index into columnsGetter's columns after which the split columns are
         * inserted. Default 0 = split columns come first. */
        splitColumnsPosition?: number;
    }): (rawData: any[], context?: IQueryContext) => BITableOptions {
        function composeData(
            rawData: any[],
            context?: IQueryContext
        ): BITableOptions {
            // throw error if context is undefined
            if (!context) {
                throw new Error('context is undefined');
            }

            const splitColumns: TableColumnItem[] = [];
            if (options.splitTableByQueryContext) {
                splitColumns.push(
                    ...TableDashboardItemHelper.getSplitColumns(context)
                );

                if (options.splitColumnsSortFn !== undefined) {
                    splitColumns.sort(options.splitColumnsSortFn);
                }

                // dynamic splitColumns can be filtered
                // generate listOfFilter for every column
                splitColumns.forEach((column: TableColumnItem) => {
                    // if column has id and nzFilterFn
                    if (column.id && column.nzFilterFn) {
                        // find column in rawData
                        const columnInRawData = rawData.find(
                            (item: { [k: string]: any }) => item[column.id]
                        );
                        // if columnInRawData is found
                        if (columnInRawData) {
                            // get all unique values for column
                            const uniqueValues = rawData
                                .map(
                                    (item: { [k: string]: any }) =>
                                        item[column.id]
                                )
                                .filter(
                                    (
                                        value: string,
                                        index: number,
                                        self: string[]
                                    ) => self.indexOf(value) === index
                                );
                            // generate listOfFilter for column
                            column.listOfFilter = uniqueValues
                                .map((value: string) => {
                                    return { text: value, value };
                                })
                                .sort();
                        }
                    }
                });
            }

            const predefinedColumns: TableColumnItem[] =
                options.columnsGetter(context);

            // add nzWidth 100px to every column
            predefinedColumns.forEach((column: TableColumnItem) => {
                column.nzWidth = '100px';
            });

            // calculate min, max of every metric
            predefinedColumns.forEach((column: TableColumnItem) => {
                const values = rawData.map((row) => row[column.id]);
                column.min = Math.min(...values);
                column.max = Math.max(...values);
            });

            const colorGetters = predefinedColumns.map(
                (column: TableColumnItem) => {
                    if (!column.colorPalette) {
                        return { id: column.id, colorGetter: () => '' };
                    }
                    const colors = generateGradientColors(
                        column.colorPalette.startColor,
                        column.colorPalette.endColor,
                        column.colorPalette.steps
                    );

                    // column.colorPalette.steps + 1 uniform ranges between min and max where n is defined by column.colorPalette.steps
                    const ranges = Array.from(
                        { length: column.colorPalette.steps + 1 },
                        (_, i) =>
                            column.min! +
                            (column.max! - column.min!) *
                                (i / column.colorPalette!.steps)
                    );

                    // a function which returns the color for a given value
                    // if value between ranges[i] and ranges[i+1] return colors[i]
                    // if value < ranges[0] return colors[0]
                    // if value > ranges[ranges.length - 1] return colors[colors.length - 1]
                    const colorGetter = (value: number) => {
                        // if value is null return color for 0
                        if (value === null) {
                            return colors[0];
                        }
                        for (let i = 0; i < ranges.length - 1; i++) {
                            if (value >= ranges[i] && value <= ranges[i + 1]) {
                                return colors[i];
                            }
                        }
                        return colors[colors.length - 1];
                    };

                    return { id: column.id, colorGetter };
                }
            );

            const splitColumnsPosition = options.splitColumnsPosition ?? 0;
            const tableColumns: TableColumnItem[] = [
                ...predefinedColumns.slice(0, splitColumnsPosition),
                ...splitColumns,
                ...predefinedColumns.slice(splitColumnsPosition),
            ];

            const dataItems: TableDataItem[] = rawData.map(
                (row: { [k: string]: any }, index: number) => {
                    // iterate over every key in row
                    for (const key in row) {
                        row[key] = {
                            value: row[key],
                            label:
                                tableColumns
                                    .find((column) => column.id === key)
                                    ?.labelFormatter?.(row[key]) ??
                                row[key] ??
                                '',
                            'background-color':
                                colorGetters
                                    .find(
                                        (colorGetter) => colorGetter.id === key
                                    )
                                    ?.colorGetter(row[key]) ?? '',
                        };
                    }
                    return { index, data: row };
                }
            );

            return { columns: tableColumns, dataset: { data: dataItems } };
        }

        return composeData;
    }

    static getSplitColumns(context: IQueryContext): TableColumnItem[] {
        const splitColumns = context!.datasourceColumns
            .filter((column) => column.state.isSplitApplied)
            .map((column): TableColumnItem => {
                const name = (column: IQueryContextDatasourceColumn) => {
                    switch (column.id) {
                        case 'FIREBASE_FIRST_OPEN_DATE':
                            return (
                                'First open ' +
                                column.state.value.splitAggregate.toLowerCase()
                            );
                        default:
                            const name = getDatasourceColumnName(column.id);
                            return (
                                name.charAt(0).toUpperCase() +
                                name.slice(1).toLowerCase()
                            );
                    }
                };

                const nzWidth = (column: IQueryContextDatasourceColumn) => {
                    switch (column.id) {
                        case 'FIREBASE_FIRST_OPEN_DATE':
                            return '100px';
                        case 'ATTRIBUTION_CAMPAIGN_NAME':
                            return '300px';
                        default:
                            return '100px';
                    }
                };

                return {
                    id: column.id,
                    nzLeft: true,
                    name: name(column),
                    nzWidth: nzWidth(column),
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        column.id,
                        'string' // assume every split is string
                    ),
                    nzFilterFn: TableDashboardItemHelper.defaultFilterFnGetter(
                        column.id
                    ),
                };
            });

        return splitColumns;
    }

    static getDefaultColorPalette() {
        return {
            startColor: '#e4f4ef',
            endColor: '#aabcce',
            steps: 6,
        };
    }

    static defaultSortFnGetter(key: string, type: 'number' | 'string') {
        switch (type) {
            case 'number':
                return (a: TableDataItem, b: TableDataItem) =>
                    (a.data[key].value as number) -
                    (b.data[key].value as number);
            default:
                return (a: TableDataItem, b: TableDataItem) =>
                    (a.data[key].value as any).localeCompare(
                        b.data[key].value as any
                    );
        }
    }

    static defaultFilterFnGetter = (key: string) => {
        const fn: NzTableFilterFn = (list: any[], item: any) => {
            return list.some(
                (name: string) =>
                    (item.data[key].value as string).indexOf(name) !== -1
            );
        };
        return fn;
    };
}
